import createChainedFunction from 'utils/createChainedFunction';
import findScrollParent from 'utils/findScrollParent';
import LayerTrigger, { Props as LayerTriggerProps } from 'components/LayerTrigger';
import { MouseEventHandler } from 'types/typescript/eventHandlers';
import React from 'react';
import ReactDOM from 'react-dom';

/**
 * ## [LayerTrigger](/internal/components/LayerTrigger) with configurable delayed hovers.
 * ### Used in conjunction with interactive [Layers](/internal/components/Layer), like [Hovercards](/internal/components/Hovercard).
 *
 * Unlike a normal `LayerTrigger`, the user is able to move their mouse from the `HoverLayerTrigger`
 * element to the layer. The layer is hidden when the user moves their mouse away from the layer
 * or performs an action within the layer.
 *
 * If the user hovers over another `HoverLayerTrigger` while moving their mouse towards a visible
 * layer, that `HoverLayerTrigger` will *not* be triggered.
 *
 *
 * ```example
 * <Buttons>
 *   <HoverLayerTrigger
 *     layer={
 *       <Hovercard>
 *         <HovercardBody>
 *           Hovercard
 *         </HovercardBody>
 *       </Hovercard>
 *     }>
 *     <Button>Hover</Button>
 *   </HoverLayerTrigger>
 *   <HoverLayerTrigger
 *     delayHide={1500}
 *     layer={
 *       <Hovercard>
 *         <HovercardBody>
 *           Delayed hide Hovercard
 *         </HovercardBody>
 *       </Hovercard>
 *     }>
 *     <Button>Hover</Button>
 *   </HoverLayerTrigger>
 *   <HoverLayerTrigger
 *     delayShow={1500}
 *     layer={
 *       <Hovercard>
 *         <HovercardBody>
 *           Delayed show Hovercard
 *         </HovercardBody>
 *       </Hovercard>
 *     }>
 *     <Button>Hover</Button>
 *   </HoverLayerTrigger>
 * </Buttons>
 * ```
 *
 *
 * # TODO
 * * Consider enforcing `focus`-able child
 * * Keyboard accessibility - focus layer & use tab isolation
 */

let _currentInstanceShown: HoverLayerTrigger | null = null;
let _showTimeout: NodeJS.Timer | null = null;
type MouseOrigin = { x: number; y: number };

class HoverLayerTrigger extends React.Component<LayerTriggerProps> {
  constructor(props: LayerTriggerProps) {
    super(props);
    this.trigger = React.createRef();
  }

  componentDidMount() {
    this._mounted = true;
    this._scrollParent = findScrollParent(ReactDOM.findDOMNode(this));
  }

  componentWillUnmount() {
    this._mounted = false;
  }

  _mounted = false;
  _mouseOrigin: MouseOrigin | null = null;
  _scrollParent: Node | Window | null = null;
  _hideTimeout: NodeJS.Timer | null = null;
  trigger: React.RefObject<LayerTrigger> | null;

  addDocumentListeners = () => {
    document.addEventListener(
      'mousemove',
      (this.handleDocumentMouseMove as unknown) as EventListenerObject,
      false
    );
  };

  handleClick = () => {
    if (_showTimeout !== null) {
      clearTimeout(_showTimeout);
      _showTimeout = null;
    }

    const { trigger } = this;
    if (trigger && trigger.current) {
      if (trigger.current.isLayerShown()) {
        trigger.current.hideLayer();
        _currentInstanceShown = null;
      }
    }
  };

  handleDelayedHide = () => {
    if (this._hideTimeout !== null) {
      clearTimeout(this._hideTimeout);
      this._hideTimeout = null;
    }

    const delay = this.props.delayHide !== null ? this.props.delayHide : this.props.delay;

    if (!delay) {
      this.hideLayer();
      return;
    }

    this._hideTimeout = setTimeout(() => {
      this._hideTimeout = null;
      this.hideLayer();
    }, delay);
  };

  handleDelayedShow = () => {
    if (_showTimeout !== null) {
      clearTimeout(_showTimeout);
      _showTimeout = null;
    }

    const delay = this.props.delayShow !== null ? this.props.delayShow : this.props.delay;

    if (!delay) {
      this.showLayer();
      return;
    }

    _showTimeout = setTimeout(() => {
      _showTimeout = null;
      this.showLayer();
    }, delay);
  };

  handleDocumentMouseMove: MouseEventHandler = event => {
    if (this.shouldHideLayer({ x: event.clientX, y: event.clientY })) {
      this.handleDelayedHide();
      this.removeDocumentListeners();
    }
  };

  handleDocumentScroll = () => {
    this.handleDelayedHide();
    this.removeDocumentListeners();
  };

  handleLayerHide = () => {
    this.removeDocumentListeners();
    _currentInstanceShown = null;
  };

  handleLayerMouseEnter = () => {
    this.removeDocumentListeners();
  };

  handleLayerMouseLeave = () => {
    this.handleDelayedHide();
  };

  handleMouseEnter = () => {
    this.handleDelayedShow();
  };

  handleMouseLeave: MouseEventHandler = event => {
    if (_showTimeout !== null) {
      clearTimeout(_showTimeout);
      _showTimeout = null;
    }

    if (_currentInstanceShown !== this) {
      return;
    }

    this._mouseOrigin = {
      x: event.clientX,
      y: event.clientY,
    };

    this.addDocumentListeners();
  };

  handleMouseMove = () => {
    this.handleDelayedShow();
  };

  hideLayer = () => {
    if (this._scrollParent) {
      this._scrollParent.removeEventListener('scroll', this.handleDocumentScroll, false);
    }

    if (this._mounted && this.trigger && this.trigger.current) {
      this.trigger.current.hideLayer();
    }
    _currentInstanceShown = null;
  };

  isPointInTriangle = (point: MouseOrigin, p1: MouseOrigin, p2: MouseOrigin, p3: MouseOrigin) => {
    // This checks to see if the mouse is moving within a triangle formed by our three points.
    // See: http://www.blackpawn.com/texts/pointinpoly/default.html
    // This would be better if the sides of the triangle weren't straight and had a parabolic-like
    // shape, but that just seemed really hard.
    // I might just adjust the origin by some amount to provide some leeway.

    const v0 = [p3.x - p1.x, p3.y - p1.y];
    const v1 = [p2.x - p1.x, p2.y - p1.y];
    const v2 = [point.x - p1.x, point.y - p1.y];

    const dot00 = v0[0] * v0[0] + v0[1] * v0[1];
    const dot01 = v0[0] * v1[0] + v0[1] * v1[1];
    const dot02 = v0[0] * v2[0] + v0[1] * v2[1];
    const dot11 = v1[0] * v1[0] + v1[1] * v1[1];
    const dot12 = v1[0] * v2[0] + v1[1] * v2[1];

    const invDenom = 1 / (dot00 * dot11 - dot01 * dot01);

    const u = (dot11 * dot02 - dot01 * dot12) * invDenom;
    const v = (dot00 * dot12 - dot01 * dot02) * invDenom;

    return u >= 0 && v >= 0 && u + v <= 1;
  };

  removeDocumentListeners = () => {
    document.removeEventListener(
      'mousemove',
      (this.handleDocumentMouseMove as unknown) as EventListenerObject,
      false
    );
  };

  shouldHideLayer = (point: MouseOrigin): boolean => {
    if (!this._mounted) {
      return true;
    }

    const { trigger } = this;
    if (trigger && trigger.current) {
      // If the layer is already hidden for some reason, ensure we clean up.
      if (!trigger.current.isLayerShown()) {
        return true;
      }
      const rect = trigger.current.getLayerRect();

      // Determine the points on the layer's rect in which the mouse can move towards.
      let p1;
      let p2;
      const origin = this._mouseOrigin;

      if (!origin) {
        return false;
      }

      if (origin.x > rect.right) {
        // Left
        p1 = {
          x: origin.y < rect.top ? rect.left : rect.right,
          y: rect.top,
        };
        p2 = {
          x: origin.y > rect.bottom ? rect.left : rect.right,
          y: rect.bottom,
        };
      } else if (origin.x < rect.left) {
        // Right
        p1 = {
          x: origin.y < rect.top ? rect.right : rect.left,
          y: rect.top,
        };
        p2 = {
          x: origin.y > rect.bottom ? rect.right : rect.left,
          y: rect.bottom,
        };
      } else if (origin.y > rect.bottom) {
        // Top
        p1 = {
          x: rect.left,
          y: origin.x < rect.left ? rect.top : rect.bottom,
        };
        p2 = {
          x: rect.right,
          y: origin.x > rect.right ? rect.top : rect.bottom,
        };
      } else if (origin.y < rect.top) {
        // Bottom
        p1 = {
          x: rect.left,
          y: origin.x < rect.left ? rect.bottom : rect.top,
        };
        p2 = {
          x: rect.right,
          y: origin.x > rect.right ? rect.bottom : rect.top,
        };
      } else {
        // Is the origin inside of the layer? Not sure how that happened.
        console.warn('origin in layer rect', origin, rect);
        // FIXME https://app.asana.com/0/42965143452863/193556816395307
        console.warn('HoverLayerTrigger non-fatal error');
        return false;
      }

      return !this.isPointInTriangle(point, origin, p1, p2);
    }

    return false;
  };

  showLayer = () => {
    // Only allow one layer to be triggered by `HoverLayerTrigger` at a time
    if (_currentInstanceShown !== null) {
      return;
    }

    if (this._mounted) {
      if (this._scrollParent) {
        this._scrollParent.addEventListener('scroll', this.handleDocumentScroll, false);
      }

      if (this.trigger && this.trigger.current) {
        this.trigger.current.showLayer();
      }
      _currentInstanceShown = this;
    }
  };

  render() {
    let child: React.ReactElement = React.Children.only(this.props.children) as React.ReactElement;

    child = React.cloneElement(child, {
      onMouseEnter: createChainedFunction(this.handleMouseEnter, child.props.onMouseEnter),
      onMouseMove: createChainedFunction(this.handleMouseMove, child.props.onMouseMove),
      onMouseLeave: createChainedFunction(this.handleMouseLeave, child.props.onMouseLeave),
      onClick: createChainedFunction(this.handleClick, child.props.onClick),
    });

    let { layer } = this.props;

    layer = React.cloneElement(layer, {
      onMouseEnter: createChainedFunction(this.handleLayerMouseEnter, layer.props.onMouseEnter),
      onMouseLeave: createChainedFunction(this.handleLayerMouseLeave, layer.props.onMouseLeave),
      onRequestHide: createChainedFunction(this.handleLayerHide, layer.props.onRequestHide),
    });

    return (
      <LayerTrigger {...this.props} layer={layer} ref={this.trigger} trigger="manual">
        {child}
      </LayerTrigger>
    );
  }
}

export default HoverLayerTrigger;
